import { toast } from "sonner";
import type { NewsAlertSortOrder } from "@/data-access/news-alerts/queries/news-alerts-queries";
import type {
  AlertCreationAndEditState,
  AlertSection,
} from "./news-alerts.slice";
import useNewsAlertsStore from "./news-alerts.slice";

const revokeNewsAlertLogoPreviewUrl = (previewUrl: string | null) => {
  if (previewUrl?.startsWith("blob:")) {
    URL.revokeObjectURL(previewUrl);
  }
};

const resetCreateEditNewsAlert = () => {
  const { createEditNewsAlert } = useNewsAlertsStore.getState();
  revokeNewsAlertLogoPreviewUrl(createEditNewsAlert.logoPreviewUrl);

  const initialState: AlertCreationAndEditState = {
    id: null,
    alertName: "",
    description: "",
    scheduleFrequency: "DAILY",
    scheduleTime: "8:00 AM",
    timezone: "",
    recipients: new Set(),
    sections: new Map(),
    logoFile: null,
    logoPreviewUrl: null,
    logoId: null,
  };

  useNewsAlertsStore.setState({ createEditNewsAlert: initialState });
};

const resetCreateEditNewsAlertSection = () => {
  const initialState: AlertSection = {
    id: "",
    name: "",
    feedId: null,
    savedViewId: null,
    articleLimit: 25,
    sortOrder: "DATE_DESC" as NewsAlertSortOrder,
  };

  useNewsAlertsStore.setState({ createEditNewsAlertSection: initialState });
};

const setIsFetchingNewsAlertById = (isFetching: boolean) => {
  useNewsAlertsStore.setState({ isFetchingNewsAlertById: isFetching });
};

const openCreateNewsAlertModal = () => {
  const { editNewsAlertId } = useNewsAlertsStore.getState();
  useNewsAlertsStore.setState({ isCreateNewsAlertModalOpen: true });
  // Only reset if we're not editing (editNewsAlertId will be set before opening for edit)
  if (!editNewsAlertId) {
    resetCreateEditNewsAlert();
  }
};

const closeCreateNewsAlertModal = () => {
  resetCreateEditNewsAlert();
  useNewsAlertsStore.setState({
    isCreateNewsAlertModalOpen: false,
    isEditingNewsAlert: false,
    editNewsAlertId: null,
    isFetchingNewsAlertById: false,
  });
};

const setIsCreatingNewsAlert = (isCreating: boolean) => {
  useNewsAlertsStore.setState({ isCreatingNewsAlert: isCreating });
};

const openCreateNewsAlertSectionModal = () => {
  useNewsAlertsStore.setState({ isCreateNewsAlertSectionModalOpen: true });
};

const closeCreateNewsAlertSectionModal = () => {
  resetCreateEditNewsAlertSection();
  useNewsAlertsStore.setState({
    isCreateNewsAlertSectionModalOpen: false,
    isEditingNewsAlertSection: false,
  });
};

const addNewSectionToNewsAlert = (section: AlertSection) => {
  const { createEditNewsAlert } = useNewsAlertsStore.getState();

  // Generate a unique key for the section using timestamp
  const sectionKey = `section-${Date.now()}`;

  // Ensure section.id matches the Map key
  const sectionWithId = { ...section, id: sectionKey };

  const newSections = createEditNewsAlert.sections.set(
    sectionKey,
    sectionWithId,
  );

  useNewsAlertsStore.setState({
    createEditNewsAlert: {
      ...createEditNewsAlert,
      sections: newSections,
    },
  });
};

const createNewsAlertSection = async (section: AlertSection) => {
  setIsCreatingNewsAlertSection(true);

  try {
    addNewSectionToNewsAlert(section);
    setIsCreatingNewsAlertSection(false);
    closeCreateNewsAlertSectionModal();

    return true;
  } catch {
    toast.error("An error occurred while creating news alert section.");
    setIsCreatingNewsAlertSection(false);
    return null;
  }
};

const updateSectionInNewsAlert = (section: AlertSection) => {
  const { createEditNewsAlert } = useNewsAlertsStore.getState();

  // replace the section with the new section
  const newSections = new Map(
    [...createEditNewsAlert.sections].map(([key, value]) => [
      key,
      key === section.id ? section : value,
    ]),
  );

  useNewsAlertsStore.setState({
    createEditNewsAlert: {
      ...createEditNewsAlert,
      sections: newSections,
    },
  });
};

const updateNewsAlertSection = async (section: AlertSection) => {
  setIsEditingNewsAlertSection(true);

  try {
    updateSectionInNewsAlert(section);
    setIsEditingNewsAlertSection(false);
    closeCreateNewsAlertSectionModal();

    return true;
  } catch {
    toast.error("An error occurred while updating news alert section.");
    setIsEditingNewsAlertSection(false);
    return null;
  }
};

const setIsCreatingNewsAlertSection = (isCreating: boolean) => {
  useNewsAlertsStore.setState({ isCreatingNewsAlertSection: isCreating });
};

const setIsEditingNewsAlertSection = (isEditing: boolean) => {
  useNewsAlertsStore.setState({ isEditingNewsAlertSection: isEditing });
};

const setCreateEditNewsAlertSection = (section: AlertSection) => {
  useNewsAlertsStore.setState({
    createEditNewsAlertSection: {
      id: section.id,
      name: section.name,
      feedId: section.feedId,
      savedViewId: section.savedViewId,
      articleLimit: section.articleLimit,
      sortOrder: section.sortOrder,
    },
  });
};

const editNewsAlertSection = (section: AlertSection) => {
  openCreateNewsAlertSectionModal();
  setIsEditingNewsAlertSection(true);
  setCreateEditNewsAlertSection(section);
};

const deleteNewsAlertSection = (sectionId: string) => {
  const { createEditNewsAlert } = useNewsAlertsStore.getState();

  // Remove section by ID from the Map
  const newSections = new Map(createEditNewsAlert.sections);
  newSections.delete(sectionId);

  useNewsAlertsStore.setState({
    createEditNewsAlert: { ...createEditNewsAlert, sections: newSections },
  });
};

const updateCreateEditNewsAlert = (alert: AlertCreationAndEditState) => {
  const { createEditNewsAlert } = useNewsAlertsStore.getState();
  useNewsAlertsStore.setState({
    createEditNewsAlert: { ...createEditNewsAlert, ...alert },
  });
};

const addRecipientToNewsAlert = (recipients: string) => {
  const { createEditNewsAlert } = useNewsAlertsStore.getState();

  // Prevent duplicate recipients
  if (createEditNewsAlert.recipients.has(recipients)) {
    return;
  }

  useNewsAlertsStore.setState({
    createEditNewsAlert: {
      ...createEditNewsAlert,
      recipients: new Set([...createEditNewsAlert.recipients, recipients]),
    },
  });
};

const removeRecipientFromNewsAlert = (recipients: string) => {
  const { createEditNewsAlert } = useNewsAlertsStore.getState();

  if (!createEditNewsAlert.recipients.has(recipients)) {
    return;
  }

  const newRecipients = new Set([
    ...Array.from(createEditNewsAlert.recipients).filter(
      (r) => r !== recipients,
    ),
  ]);

  useNewsAlertsStore.setState({
    createEditNewsAlert: {
      ...createEditNewsAlert,
      recipients: newRecipients,
    },
  });
};

const setIsEditingNewsAlert = (isEditing: boolean) => {
  useNewsAlertsStore.setState({ isEditingNewsAlert: isEditing });
};

const setDeleteNewsAlertId = (id: string) => {
  useNewsAlertsStore.setState({ deleteNewsAlertId: id });
};

const setEditNewsAlertId = (id: string | null) => {
  useNewsAlertsStore.setState({ editNewsAlertId: id });
};

const openDeleteNewsAlertModal = () => {
  useNewsAlertsStore.setState({ isDeleteNewsAlertModalOpen: true });
};

const closeDeleteNewsAlertModal = () => {
  useNewsAlertsStore.setState({
    isDeleteNewsAlertModalOpen: false,
    deleteNewsAlertId: null,
  });
  setNewsAlertName("");
};

const setNewsAlertName = (name: string) => {
  const { createEditNewsAlert } = useNewsAlertsStore.getState();
  useNewsAlertsStore.setState({
    createEditNewsAlert: {
      ...createEditNewsAlert,
      alertName: name,
    },
  });
};

const setIsGeneratingNewsAlertPreview = (isGenerating: boolean) => {
  useNewsAlertsStore.setState({ isGeneratingNewsAlertPreview: isGenerating });
};

const setIsUploadingNewsAlertLogo = (isUploading: boolean) => {
  useNewsAlertsStore.setState({ isUploadingNewsAlertLogo: isUploading });
};

const setUploadedNewsAlertLogo = (logo: File) => {
  const { createEditNewsAlert } = useNewsAlertsStore.getState();
  revokeNewsAlertLogoPreviewUrl(createEditNewsAlert.logoPreviewUrl);

  useNewsAlertsStore.setState({
    createEditNewsAlert: {
      ...createEditNewsAlert,
      logoFile: logo,
      logoPreviewUrl: URL.createObjectURL(logo),
    },
  });
};

const setNewsAlertLogoPreviewUrl = (url: string) => {
  const { createEditNewsAlert } = useNewsAlertsStore.getState();
  revokeNewsAlertLogoPreviewUrl(createEditNewsAlert.logoPreviewUrl);

  useNewsAlertsStore.setState({
    createEditNewsAlert: {
      ...createEditNewsAlert,
      logoPreviewUrl: url,
    },
  });
};

const setNewsAlertLogoId = (id: string) => {
  const { createEditNewsAlert } = useNewsAlertsStore.getState();
  useNewsAlertsStore.setState({
    createEditNewsAlert: {
      ...createEditNewsAlert,
      logoId: id,
    },
  });
};

const removeNewsAlertLogo = () => {
  const { createEditNewsAlert } = useNewsAlertsStore.getState();
  revokeNewsAlertLogoPreviewUrl(createEditNewsAlert.logoPreviewUrl);

  useNewsAlertsStore.setState({
    createEditNewsAlert: {
      ...createEditNewsAlert,
      logoFile: null,
      logoPreviewUrl: null,
      logoId: null,
    },
  });
};

const resetCreateEditNewsAlertLogo = () => {
  setIsUploadingNewsAlertLogo(false);
  removeNewsAlertLogo();
};

export {
  addRecipientToNewsAlert,
  closeCreateNewsAlertModal,
  closeCreateNewsAlertSectionModal,
  closeDeleteNewsAlertModal,
  createNewsAlertSection,
  deleteNewsAlertSection,
  editNewsAlertSection,
  openCreateNewsAlertModal,
  openCreateNewsAlertSectionModal,
  openDeleteNewsAlertModal,
  removeNewsAlertLogo,
  removeRecipientFromNewsAlert,
  resetCreateEditNewsAlert,
  resetCreateEditNewsAlertLogo,
  setCreateEditNewsAlertSection,
  setDeleteNewsAlertId,
  setEditNewsAlertId,
  setIsCreatingNewsAlert,
  setIsEditingNewsAlert,
  setIsFetchingNewsAlertById,
  setIsGeneratingNewsAlertPreview,
  setIsUploadingNewsAlertLogo,
  setNewsAlertLogoId,
  setNewsAlertLogoPreviewUrl,
  setNewsAlertName,
  setUploadedNewsAlertLogo,
  updateCreateEditNewsAlert,
  updateNewsAlertSection,
};
